import { useCallback, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';

const useFileUpload = ({
  acceptedFileTypes,
  maxFileSize,
  onFileSelect,
  pauseStrictMode,
  resumeStrictMode,
  uploadedFile
}) => {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(
    uploadedFile?.originalFileName ? { name: uploadedFile.originalFileName } : null
  );

  const validateFile = useCallback(
    (file) => {
      if (!file) return { isValid: false, error: 'No file selected' };

      // Check file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidType = acceptedFileTypes.some((type) =>
        type.toLowerCase().includes(fileExtension)
      );

      if (!isValidType) {
        return {
          isValid: false,
          error: `Invalid file type. Accepted formats are: ${acceptedFileTypes.join(', ')}`
        };
      }

      // Check file size (maxFileSize should be in MB)
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > maxFileSize) {
        return {
          isValid: false,
          error: `File size exceeds ${maxFileSize}MB limit`
        };
      }

      return { isValid: true, error: null };
    },
    [acceptedFileTypes, maxFileSize]
  );

  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      resumeStrictMode?.();

      if (!file) return;

      const validation = validateFile(file);

      if (validation.isValid) {
        setSelectedFile(file);
        onFileSelect(file);
      } else {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
        toast({
          title: 'Invalid File',
          description: validation.error,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
    },
    [validateFile, onFileSelect, resumeStrictMode, toast]
  );

  const handleFilePickerCancel = useCallback(() => {
    resumeStrictMode?.();
  }, [resumeStrictMode]);

  const handleFileRemoved = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null);
  }, [onFileSelect]);

  const triggerFileSelect = useCallback(() => {
    if (!fileInputRef.current) return;
    pauseStrictMode?.();
    fileInputRef.current.click();
  }, [pauseStrictMode]);

  return {
    fileInputRef,
    triggerFileSelect,
    handleFileSelect,
    selectedFile,
    handleFileRemoved,
    handleFilePickerCancel
  };
};

export default useFileUpload;
